import { useState, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import api, { errText } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { DateField } from "./DateField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const OP = {
  attivo: { label: "Attivo", dot: "#2E7D5B", bg: "#E7F3EC", fg: "#1B5E20", border: "#A5D6A7" },
  temp_indisponibile: { label: "Temporaneamente fermo", dot: "#E08A00", bg: "#FCF3E2", fg: "#8A5A00", border: "#FFE082" },
  ritirato: { label: "Ritirato dall'attività", dot: "#2563A8", bg: "#E8F0FA", fg: "#1B4B82", border: "#BBDEFB" },
};
const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const today = () => new Date().toISOString().slice(0, 10);

export function OperationalBadge({ status }) {
  const o = OP[status || "attivo"] || OP.attivo;
  return (
    <span data-testid="operational-badge" className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold border" style={{ background: o.bg, color: o.fg, borderColor: o.border }}>
      <span className="h-2 w-2 rounded-full" style={{ background: o.dot }} /> {o.label}
    </span>
  );
}

// Compact one-line summary of the current unavailability (reason · return · reevaluation · notes).
export function operationalHint(horse) {
  const u = horse.unavailability || {};
  const parts = [];
  if (u.reason) parts.push(u.reason);
  if (u.prognosis_min_date && u.prognosis_max_date) parts.push(`Rientro previsto: ${fmt(u.prognosis_min_date)} – ${fmt(u.prognosis_max_date)}`);
  else if (u.expected_return) parts.push(`Rientro previsto: ${fmt(u.expected_return)}`);
  else parts.push("Nessuna data di rientro prevista");
  if (u.reevaluation_date) parts.push(`Rivalutazione: ${fmt(u.reevaluation_date)}`);
  if (u.notes) parts.push(u.notes);
  return parts.join(" · ");
}

// Display-only operational status (badge + compact prognosis). No dialogs, no triggers.
export function OperationalStatusDisplay({ horse, className }) {
  const cur = horse.operational_status || "attivo";
  const u = horse.unavailability || {};
  const returnHint = () => {
    if (u.prognosis_min_date && u.prognosis_max_date) return `Rientro indicativo: tra ${fmt(u.prognosis_min_date)} e ${fmt(u.prognosis_max_date)}`;
    if (u.expected_return) return `Rientro previsto: ${fmt(u.expected_return)}`;
    return "Nessuna data di rientro prevista";
  };
  return (
    <div className={className} data-testid="operational-status-display">
      <OperationalBadge status={cur} />
      {cur === "temp_indisponibile" && (
        <p className="text-[11px] text-[#8A5A00] mt-1" data-testid="operational-detail">
          {u.reason ? `${u.reason} · ` : ""}dal {fmt(u.start_date)}{u.reevaluation_date ? ` · rivalutazione ${fmt(u.reevaluation_date)}` : ""}<br />{returnHint()}
        </p>
      )}
    </div>
  );
}

// Headless control: owns the operational-status dialogs (full prognosis form) and is
// driven imperatively via ref (openTemp/openActive/openRetire). Renders no visible trigger.
const OperationalStatusControl = forwardRef(function OperationalStatusControl({ horse, stableId, canWrite, onChange }, ref) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({});
  const [warn, setWarn] = useState(null);
  const navigate = useNavigate();
  const cur = horse.operational_status || "attivo";
  const u = horse.unavailability || {};

  const start = (form) => {
    if (form === "temp_indisponibile") {
      setF({ status: "temp_indisponibile", start_date: u.start_date || today(), reason: u.reason || "",
             prog_mode: u.prognosis_min_days ? "range" : (u.expected_return ? "single" : "none"),
             prognosis_min_days: u.prognosis_min_days || "", prognosis_max_days: u.prognosis_max_days || "",
             expected_return: u.expected_return || "", reevaluation_date: u.reevaluation_date || "", notes: u.notes || "" });
    } else {
      setF({ status: form, end_date: today(), end_notes: "" });
    }
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openTemp: () => start("temp_indisponibile"),
    openRetire: () => start("ritirato"),
    openActive: () => start("attivo"),
  }));

  const submit = async (confirmed = false) => {
    if (f.status === "temp_indisponibile" && !confirmed) {
      try {
        const from = f.start_date || today();
        const to = f.prog_mode === "single" ? f.expected_return : null;
        const { data } = await api.get(`/stables/${stableId}/horses/${horse.id}/scheduled-usage`, { params: { date_from: from, ...(to ? { date_to: to } : {}) } });
        if (data.count > 0) { setWarn({ count: data.count }); return; }
      } catch { /* informational only */ }
    }
    setBusy(true);
    try {
      const body = { status: f.status };
      if (f.status === "temp_indisponibile") {
        body.start_date = f.start_date || today();
        body.reason = f.reason || null;
        body.notes = f.notes || null;
        body.reevaluation_date = f.reevaluation_date || null;
        if (f.prog_mode === "range") {
          body.prognosis_min_days = f.prognosis_min_days ? Number(f.prognosis_min_days) : null;
          body.prognosis_max_days = f.prognosis_max_days ? Number(f.prognosis_max_days) : null;
        } else if (f.prog_mode === "single") {
          body.expected_return = f.expected_return || null;
        }
      } else if (cur === "temp_indisponibile") {
        body.end_date = f.end_date || today();
        body.end_notes = f.end_notes || null;
      }
      await api.post(`/stables/${stableId}/horses/${horse.id}/operational-status`, body);
      toast.success("Stato operativo aggiornato");
      setWarn(null); setOpen(false);
      onChange && onChange();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!canWrite) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="op-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-display">
              {f.status === "temp_indisponibile" ? "Temporaneamente indisponibile" : f.status === "ritirato" ? "Ritira dall'attività" : "Rientro in attività"}
            </DialogTitle>
            {f.status === "temp_indisponibile" && (
              <DialogDescription className="text-xs" data-testid="op-help-temp">
                Il cavallo non verrà proposto normalmente nel Calendario, ma potrà comunque essere assegnato manualmente. Tutti i dettagli sono facoltativi.
              </DialogDescription>
            )}
            {f.status === "ritirato" && (
              <DialogDescription className="text-xs">
                Il cavallo resta presente in anagrafica e nello storico, ma esce dalla disponibilità normale. Potrai riportarlo Attivo in seguito.
              </DialogDescription>
            )}
          </DialogHeader>

          {f.status === "temp_indisponibile" && (
            <div className="space-y-3">
              <div><Label>Data inizio</Label>
                <DateField className="mt-1.5" testId="op-start-date" value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} allowFuture /></div>
              <div><Label>Motivo (opzionale)</Label>
                <Select value={f.reason || "none"} onValueChange={(v) => setF({ ...f, reason: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="op-reason"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {["Veterinario", "Infortunio", "Convalescenza", "Riposo", "Altro"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label>Prognosi / rientro (opzionale)</Label>
                <Select value={f.prog_mode || "none"} onValueChange={(v) => setF({ ...f, prog_mode: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="op-prog-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna previsione</SelectItem>
                    <SelectItem value="single">Data prevista di rientro</SelectItem>
                    <SelectItem value="range">Intervallo di riposo (giorni)</SelectItem>
                  </SelectContent>
                </Select></div>
              {f.prog_mode === "single" && (
                <div><Label>Data prevista di rientro</Label>
                  <DateField className="mt-1.5" testId="op-expected-return" value={f.expected_return} onChange={(v) => setF({ ...f, expected_return: v })} allowFuture /></div>
              )}
              {f.prog_mode === "range" && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Giorni min</Label><Input type="number" min="0" className="mt-1.5" data-testid="op-prog-min" value={f.prognosis_min_days} onChange={(e) => setF({ ...f, prognosis_min_days: e.target.value })} /></div>
                  <div><Label>Giorni max</Label><Input type="number" min="0" className="mt-1.5" data-testid="op-prog-max" value={f.prognosis_max_days} onChange={(e) => setF({ ...f, prognosis_max_days: e.target.value })} /></div>
                </div>
              )}
              <div><Label>Data rivalutazione (opzionale)</Label>
                <DateField className="mt-1.5" testId="op-reeval" value={f.reevaluation_date} onChange={(v) => setF({ ...f, reevaluation_date: v })} allowFuture /></div>
              <div><Label>Note (opzionale)</Label>
                <Textarea className="mt-1.5" data-testid="op-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
            </div>
          )}

          {f.status === "attivo" && cur === "temp_indisponibile" && (
            <div className="space-y-3">
              <div><Label>Data effettiva di rientro</Label>
                <DateField className="mt-1.5" testId="op-end-date" value={f.end_date} onChange={(v) => setF({ ...f, end_date: v })} /></div>
              <div><Label>Nota (opzionale)</Label>
                <Textarea className="mt-1.5" data-testid="op-end-notes" value={f.end_notes} onChange={(e) => setF({ ...f, end_notes: e.target.value })} /></div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={() => submit(false)} disabled={busy} data-testid="op-confirm" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!warn} onOpenChange={(o) => !o && setWarn(null)}>
        <DialogContent data-testid="op-warn-dialog">
          <DialogHeader><DialogTitle className="font-display text-[#B45309]">Lezioni già programmate</DialogTitle>
            <DialogDescription className="text-sm text-[#5F6B64]" data-testid="op-warn-text">
              {horse.display_name} risulta già assegnato a {warn?.count} utilizzo/i nel periodo di indisponibilità. Nessuna lezione verrà modificata automaticamente.
            </DialogDescription></DialogHeader>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" data-testid="op-warn-calendar" onClick={() => navigate("/calendario")}>Vedi nel Calendario</Button>
            <Button onClick={() => submit(true)} disabled={busy} data-testid="op-warn-continue" className="bg-[#1B3B2B] hover:bg-[#142E1F]">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Continua comunque</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default OperationalStatusControl;
